import {
  Currency,
  ImpactShopProductType,
  ImpactType,
  impactTypeToProjectTypeMapper,
  UK_PRODUCT_SLUGS,
  User,
  isBaftaUser,
} from "@ecologi/shared";
import React, { useState } from "react";
import { useRouter } from "next/router";
import { animateScroll as scroller } from "react-scroll";
import { Button } from "../Button";
import { ImpactShopProductCard } from "../ImpactShopProductCard/ImpactShopProductCard";
import { UnsureWhatToFundCard } from "../ImpactShopProductCard/UnsureWhatToFundCard";
import { UnsureWhatToFundModal } from "../UnsureWhatToFundModal/UnsureWhatToFundModal";
import styled from "@emotion/styled";
import { CARD_WIDTH } from "../ImpactShopProductCard/ImpactShopProductCard.styles";
import { ImpactShopFilterBar } from "./ImpactShopFilterBar";
import { useImpactShopFilter } from "./useImpactShopFilter";

const BottomSectionContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 32px 0 48px;
`;

const ImpactShopContentContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 16px;
  padding: 0 16px;
  background-color: ${({ theme }) => theme.colors.mist};

  @media ${({ theme }) => theme.bp.tabletUp} {
    grid-template-columns: repeat(auto-fill, minmax(${CARD_WIDTH}px, 1fr));
  }

  @media ${({ theme }) => theme.bp.wideDesktopUp} {
    grid-gap: 24px;
    padding: 0 24px;
  }
`;

const handleBackToTop = () => {
  scroller.scrollToTop();
};

export type ImpactShopHomeContentProps = {
  currency: Currency;
  products: ImpactShopProductType[];
  onProductClick?: (title: string) => void;
  onFilterClick: (filterName: string) => void;
  user?: User;
  onAnalyticsEvent?: (
    eventName: string,
    props: Record<string, string | number | boolean>
  ) => void;
};

export const ImpactShopHomeContent = ({
  currency: BASKET_CURRENCY,
  products,
  onProductClick,
  onFilterClick,
  user,
  onAnalyticsEvent,
}: ImpactShopHomeContentProps) => {
  const router = useRouter();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const {
    impactTypes,
    toggleImpactType,
    clearImpactTypes,
    isUkOnly,
    setIsUkOnly,
  } = useImpactShopFilter();

  const handleCardClick = (slug: string) => () => {
    router.push(`/impact-shop/${slug}`);
    onProductClick?.(slug);
  };

  const handleUnsureCardClick = () => {
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  // Pre-filter products to get only visible products
  const visibleProducts = products.filter((product) => {
    const price = product.prices.find(
      (price) => price.currency == BASKET_CURRENCY
    )?.amount;
    if (!price) return false;
    return !shouldFilterProduct(product, impactTypes, isUkOnly);
  });

  // Split into first 2 and remaining
  const firstTwoProducts = visibleProducts.slice(0, 2);
  const remainingProducts = visibleProducts.slice(2);

  // Determine if we should show the UnsureWhatToFundCard
  // Don't show to BAFTA users, but show to everyone else (including unauthenticated)
  const shouldShowUnsureCard = !user || !isBaftaUser(user);

  return (
    <>
      <ImpactShopFilterBar
        impactTypes={impactTypes}
        toggleImpactType={toggleImpactType}
        clearImpactTypes={clearImpactTypes}
        isUkOnly={isUkOnly}
        setIsUkOnly={setIsUkOnly}
        onFilterClick={onFilterClick}
      />
      <ImpactShopContentContainer>
        {firstTwoProducts.map((product) => {
          const price = product.prices.find(
            (price) => price.currency == BASKET_CURRENCY
          )!.amount;

          return (
            <ImpactShopProductCard
              badgePath={product.display.badgePath}
              currency={BASKET_CURRENCY}
              description={product.display.snippet || ""}
              image={product.display.images[0]}
              isOutOfStock={product.isOutOfStock}
              key={product.slug}
              onCardClick={handleCardClick(product.slug)}
              price={price}
              tags={product.display.tags}
              title={product.display.title}
              unit={product.display.unitType}
              minQuantity={product.minQuantity}
              productType={product.productType}
            />
          );
        })}

        {shouldShowUnsureCard && (
          <UnsureWhatToFundCard onClick={handleUnsureCardClick} />
        )}

        {remainingProducts.map((product) => {
          const price = product.prices.find(
            (price) => price.currency == BASKET_CURRENCY
          )!.amount;

          return (
            <ImpactShopProductCard
              badgePath={product.display.badgePath}
              currency={BASKET_CURRENCY}
              description={product.display.snippet || ""}
              image={product.display.images[0]}
              isOutOfStock={product.isOutOfStock}
              key={product.slug}
              onCardClick={handleCardClick(product.slug)}
              price={price}
              tags={product.display.tags}
              title={product.display.title}
              unit={product.display.unitType}
              minQuantity={product.minQuantity}
              productType={product.productType}
            />
          );
        })}
      </ImpactShopContentContainer>
      <BottomSectionContainer>
        <Button
          border
          onClick={handleBackToTop}
          text={"Back to top"}
          textOnly
        />
      </BottomSectionContainer>
      <UnsureWhatToFundModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        products={products}
        onAnalyticsEvent={onAnalyticsEvent}
      />
    </>
  );
};

const shouldFilterProduct = (
  product: ImpactShopProductType,
  impactTypes: ImpactType[],
  isUkOnly: boolean
) => {
  if (isUkOnly) {
    return !UK_PRODUCT_SLUGS.includes(product.slug);
  }

  if (impactTypes.length > 0) {
    const matchesAnyFilter = impactTypes.some((impactType) =>
      product.display.solutions?.some((solution) =>
        impactTypeToProjectTypeMapper[impactType].includes(solution)
      )
    );
    return !matchesAnyFilter;
  }

  return false;
};
